import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { ArrowRight, ChevronRight, ChevronDown, ChevronUp, Check, Star, Share2, Newspaper, FileText, Image, PlayCircle, Quote, Loader2 } from 'lucide-react';
import Icon from './Icon';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const PublicPage = () => {
  const { slug } = useParams();
  const { user, logout } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navPages, setNavPages] = useState([]);
  const [error, setError] = useState(null);

  const curatedNavPages = useMemo(() => {
    const seen = new Set();
    return navPages.filter((p) => {
      if (seen.has(p.slug)) return false;
      seen.add(p.slug);
      return true;
    });
  }, [navPages]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [pageRes, navRes] = await Promise.all([
          axios.get(`${API}/pages/${slug}`),
          axios.get(`${API}/pages`)
        ]);
        setPage(pageRes.data.page);
        setNavPages(navRes.data.pages?.filter(p => p.show_in_nav) || []);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Page not found' : 'Failed to load page');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-white mb-4">{error || 'Page not found'}</h1>
        <Link to="/" className="text-orange-400 hover:text-orange-300">Go back home</Link>
      </div>
    );
  }

  return (
    <div className="hsx-landing-root min-h-screen">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#070a12]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center h-16">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/favicon-192x192.png?v=20260609"
                alt="HopSeat" className="h-8 w-8 object-contain" />
              <span className="text-xl font-bold text-white">HopSeat</span>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              {curatedNavPages.map(p => (
                <Link key={p.slug} to={`/p/${p.slug}`}
                  className={`text-sm font-medium transition-colors ${p.slug === slug ? 'text-orange-400' : 'text-gray-400 hover:text-white'}`}
                  data-testid={`nav-${p.slug}`}>
                  {p.title}
                </Link>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/dashboard" className="text-sm text-gray-400 hover:text-white transition-colors">Dashboard</Link>
                <button onClick={logout} className="text-sm bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors">Log Out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-sm text-gray-400 hover:text-white transition-colors">Login</Link>
                <Link to="/register" className="text-sm bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Page Blocks */}
      {(page.blocks || []).filter(b => b.visible !== false).map((block) => (
        <PublicBlock key={block.id} block={block} user={user} />
      ))}

      {/* Footer */}
      <footer className="bg-[#060a12] border-t border-white/10 py-8 px-4 text-center">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-2 mb-4 md:mb-0">
            <img src="/favicon-192x192.png?v=20260609"
              alt="HopSeat" className="h-6 w-6" />
            <span className="text-lg font-bold text-white">HopSeat</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            {curatedNavPages.map(p => (
              <Link key={p.slug} to={`/p/${p.slug}`} className="hover:text-gray-300 transition-colors">{p.title}</Link>
            ))}
          </div>
          <p className="text-gray-600 text-xs mt-4 md:mt-0">&copy; 2026 HopSeat</p>
        </div>
      </footer>
    </div>
  );
};


const PublicBlock = ({ block, user }) => {
  const c = block.content || {};
  switch (block.type) {
    case 'hero': return <PubHero c={c} />;
    case 'text': return <PubText c={c} />;
    case 'image': return <PubImage c={c} />;
    case 'features': return <PubFeatures c={c} />;
    case 'pricing':
      if (!user) return <PubPricingTeaser />;
      if (user.role !== 'driver') return <PubPortalPricingNotice role={user.role} />;
      return <PubPricing c={c} />;
    case 'testimonials': return <PubTestimonials c={c} />;
    case 'cta': return <PubCTA c={c} />;
    case 'resource_center': return <PubResourceCenter c={c} />;
    case 'divider': return <PubDivider c={c} />;
    case 'faq': return <PubFAQ c={c} />;
    case 'blog_feed': return <PubBlogFeed c={c} />;
    case 'social_links': return <PubSocialLinks c={c} />;
    case 'news_feed': return <PubNewsFeed c={c} />;
    case 'video': return <PubVideo c={c} />;
    default: return null;
  }
};

const PubHero = ({ c }) => (
  <section className={`relative py-24 px-4 text-center overflow-hidden ${c.bg_style === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-900 via-black to-gray-800'}`}>
    <div className="absolute inset-0 bg-gradient-to-br from-orange-600/15 via-transparent to-orange-900/10" />
    <div className="absolute top-0 left-0 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
    <div className="relative z-10 max-w-4xl mx-auto">
      <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
        {c.title} {c.title_accent && <span className="text-orange-500 block">{c.title_accent}</span>}
      </h1>
      <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">{c.description}</p>
      <div className="flex gap-4 justify-center flex-wrap">
        {c.cta_primary_text && (
          <Link to={c.cta_primary_link || '/register'} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg shadow-orange-500/20 inline-flex items-center gap-2 hover:from-orange-600 hover:to-orange-700 transition-all">
            {c.cta_primary_text} <ArrowRight className="h-5 w-5" />
          </Link>
        )}
        {c.cta_secondary_text && (
          <Link to={c.cta_secondary_link || '/register'} className="border-2 border-orange-500 text-white px-8 py-4 rounded-lg text-lg hover:bg-orange-500/10 transition-all">
            {c.cta_secondary_text}
          </Link>
        )}
      </div>
    </div>
  </section>
);

const PubText = ({ c }) => (
  <section className="py-16 px-4 bg-gray-900">
    <div className={`max-w-4xl mx-auto text-${c.alignment || 'left'}`}>
      {c.heading && <h2 className="text-3xl font-bold text-white mb-4">{c.heading}</h2>}
      <p className="text-lg text-gray-300 leading-relaxed whitespace-pre-wrap">{c.body}</p>
    </div>
  </section>
);

const PubImage = ({ c }) => {
  const widthCls = { full: 'max-w-none', large: 'max-w-5xl', medium: 'max-w-3xl', small: 'max-w-lg' };
  return (
    <section className="py-12 px-4 bg-gray-900">
      <div className={`mx-auto ${widthCls[c.width] || 'max-w-5xl'}`}>
        {c.url && <img src={c.url} alt={c.alt || ''} className="w-full rounded-xl shadow-xl" />}
        {c.caption && <p className="text-gray-500 text-sm text-center mt-3">{c.caption}</p>}
      </div>
    </section>
  );
};

const PubFeatures = ({ c }) => (
  <section className="py-20 px-4 bg-gray-900">
    <div className="max-w-7xl mx-auto">
      <h2 className="text-4xl font-bold text-white text-center mb-2">{c.title}</h2>
      {c.subtitle && <p className="text-gray-400 text-center mb-12 text-lg">{c.subtitle}</p>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(c.cards || []).map((card, i) => (
          <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-orange-500/30 transition-all">
            <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
            <p className="text-gray-400">{card.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PubPricing = ({ c }) => (
  <section className="py-20 px-4 bg-gray-900" data-testid="pricing-section">
    <div className="max-w-7xl mx-auto">
      <h2 className="text-4xl font-bold text-white text-center mb-2">{c.title}</h2>
      {c.subtitle && <p className="text-gray-400 text-center mb-12 text-lg">{c.subtitle}</p>}
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {(c.plans || []).map((plan, i) => (
          <div key={i} className={`rounded-2xl p-8 border-2 ${plan.highlighted ? 'border-orange-500 bg-orange-500/5 relative' : 'border-gray-700 bg-gray-800/40'}`}>
            {plan.highlighted && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full">Most Popular</span>
            )}
            <h3 className="text-white font-bold text-xl mb-1">{plan.name}</h3>
            <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
            <div className="mb-6">
              <span className="text-4xl font-black text-white">{plan.price}</span>
              <span className="text-gray-500">{plan.period}</span>
            </div>
            <ul className="space-y-2 mb-8">
              {(plan.features || []).map((f, fi) => (
                <li key={fi} className="text-gray-300 text-sm flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Link to={plan.cta_link || '/register'}
              className={`block text-center py-3 rounded-lg font-semibold transition-all ${
                plan.highlighted ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}>
              {plan.cta_text}
            </Link>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PubPricingTeaser = () => (
  <section className="py-16 px-4 bg-[#0f1728]" data-testid="public-pricing-teaser-section">
    <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
      <h2 className="text-3xl font-bold text-white">Membership plans are role-aware</h2>
      <p className="mt-3 text-gray-300">
        Driver plan options are available after sign in, with recommendations tailored to experience level and activity.
      </p>
      <Link to="/login" className="inline-flex mt-5 items-center gap-2 text-orange-300 hover:text-orange-200 font-medium" data-testid="public-pricing-teaser-login-link">
        Sign in to view your plan options <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </section>
);

const PubPortalPricingNotice = ({ role }) => {
  const roleLabel = role === 'bus_company' ? 'Bus Company' : role === 'guest_tour' ? 'Tour Organizer' : 'your';
  return (
    <section className="py-16 px-4 bg-[#0f1728]" data-testid="public-portal-pricing-notice-section">
      <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
        <h2 className="text-3xl font-bold text-white">Plan details are managed in your portal</h2>
        <p className="mt-3 text-gray-300">
          {roleLabel} pricing and tier controls are available inside your authenticated dashboard settings.
        </p>
        <Link to="/dashboard" className="inline-flex mt-5 items-center gap-2 text-orange-300 hover:text-orange-200 font-medium" data-testid="public-portal-pricing-dashboard-link">
          Open dashboard plan settings <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};

const PubTestimonials = ({ c }) => (
  <section className="py-20 px-4 bg-gray-800/50">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-4xl font-bold text-white text-center mb-12">{c.title}</h2>
      <div className="space-y-6">
        {(c.items || []).map((t, i) => (
          <div key={i} className="bg-gray-800/60 rounded-2xl p-8 border border-gray-700/50">
            <div className="flex items-start gap-4">
              <Quote className="h-8 w-8 text-orange-500/40 flex-shrink-0" />
              <div>
                <p className="text-gray-300 text-lg italic leading-relaxed mb-4">{t.quote}</p>
                <div className="flex items-center gap-3">
                  {t.image_url && <img src={t.image_url} alt={t.author} className="w-10 h-10 rounded-full object-cover border-2 border-orange-500" />}
                  <span className="text-orange-400 font-semibold">{t.author}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PubCTA = ({ c }) => (
  <section className="py-20 px-4 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-center relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-orange-600/5 via-transparent to-orange-600/5" />
    <div className="relative z-10 max-w-4xl mx-auto">
      <h2 className="text-4xl font-bold text-white mb-4">{c.title}</h2>
      <p className="text-xl text-gray-300 mb-8">{c.description}</p>
      <Link to={c.cta_link || '/register'}
        className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg shadow-orange-500/20 hover:from-orange-600 hover:to-orange-700 transition-all inline-block">
        {c.cta_text}
      </Link>
    </div>
  </section>
);

const PubResourceCenter = ({ c }) => (
  <section className="py-20 px-4 bg-gray-950 relative" data-testid="resource-center-section">
    <div className="max-w-5xl mx-auto text-center">
      {c.badge && <span className="inline-block bg-orange-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium px-4 py-1.5 rounded-full mb-4">{c.badge}</span>}
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">{c.title}</h2>
      <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">{c.description}</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {(c.items || []).map((item, i) => (
          <div key={i} className="bg-gray-800/60 border border-gray-700/50 rounded-xl p-5 text-center hover:border-orange-500/30 transition-all">
            <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-3">
              <Icon name={item.icon || 'star'} size={22} className="text-orange-400" />
            </div>
            <h4 className="text-white font-medium text-sm">{item.label}</h4>
            <p className="text-gray-500 text-xs mt-1">{item.desc}</p>
          </div>
        ))}
      </div>
      {c.cta_text && (
        <div className="mt-8">
          <Link to={c.cta_link || '/register'} className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-medium transition-colors">
            {c.cta_text} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  </section>
);

const PubDivider = ({ c }) => {
  const spacing = { sm: 'py-6', md: 'py-12', lg: 'py-20' };
  return (
    <div className={`${spacing[c.spacing] || 'py-12'} px-4 bg-gray-900`}>
      {c.style === 'line' && <hr className="border-gray-700/50 max-w-4xl mx-auto" />}
      {c.style === 'dots' && <p className="text-center text-gray-600 text-2xl tracking-[1em]">...</p>}
    </div>
  );
};

const PubFAQ = ({ c }) => {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-20 px-4 bg-gray-900" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold text-white text-center mb-12">{c.title}</h2>
        <div className="space-y-3">
          {(c.items || []).map((item, i) => (
            <div key={i} className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-800/80 transition-colors">
                <span className="text-white font-medium pr-4">{item.question}</span>
                {open === i ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}
              </button>
              {open === i && <div className="px-5 pb-5 text-gray-400 leading-relaxed">{item.answer}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PubBlogFeed = ({ c }) => {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/blog/posts?limit=${c.count || 6}`).then(res => setPosts(res.data.posts || [])).catch(() => {});
  }, [c.count]);

  return (
    <section className="py-20 px-4 bg-gray-900" data-testid="blog-section">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl font-bold text-white text-center mb-12">{c.title}</h2>
        {posts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(post => (
              <div key={post.id} className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden hover:border-orange-500/30 transition-all">
                {post.image_url && <img src={post.image_url} alt={post.title} className="w-full h-48 object-cover" />}
                <div className="p-5">
                  <h3 className="text-white font-bold mb-2">{post.title}</h3>
                  <p className="text-gray-400 text-sm">{post.excerpt}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center text-lg">No blog posts yet. Check back soon!</p>
        )}
      </div>
    </section>
  );
};

const PubSocialLinks = ({ c }) => {
  const platformColors = {
    facebook: 'hover:border-blue-500/50 hover:bg-blue-500/5',
    twitter: 'hover:border-sky-500/50 hover:bg-sky-500/5',
    instagram: 'hover:border-pink-500/50 hover:bg-pink-500/5',
    youtube: 'hover:border-red-500/50 hover:bg-red-500/5',
    linkedin: 'hover:border-blue-600/50 hover:bg-blue-600/5',
    tiktok: 'hover:border-pink-400/50 hover:bg-pink-400/5'
  };
  return (
    <section className="py-20 px-4 bg-gray-900" data-testid="social-links-section">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-4xl font-bold text-white text-center mb-12">{c.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {(c.links || []).map((link, i) => (
            <a key={i} href={link.url || '#'} target="_blank" rel="noopener noreferrer"
              className={`bg-gray-800/60 border border-gray-700/50 rounded-xl p-6 text-center transition-all ${platformColors[link.platform] || 'hover:border-orange-500/50'}`}>
              <Share2 className="h-8 w-8 text-orange-400 mx-auto mb-3" />
              <h4 className="text-white font-semibold">{link.label || link.platform}</h4>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const PubNewsFeed = ({ c }) => (
  <section className="py-20 px-4 bg-gray-900" data-testid="news-section">
    <div className="max-w-4xl mx-auto">
      <h2 className="text-4xl font-bold text-white text-center mb-12">{c.title}</h2>
      <div className="space-y-4">
        {(c.items || []).map((item, i) => (
          <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 flex gap-6 items-start hover:border-orange-500/30 transition-all">
            {item.image_url ? (
              <img src={item.image_url} alt={item.title} className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-24 h-24 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                <Newspaper className="h-8 w-8 text-gray-700" />
              </div>
            )}
            <div className="flex-1">
              <h3 className="text-white font-bold text-lg">{item.title}</h3>
              {item.date && <p className="text-gray-500 text-sm mt-1">{item.date}</p>}
              <p className="text-gray-400 mt-2">{item.summary}</p>
              {item.link && (
                <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 text-sm mt-2 inline-flex items-center gap-1">
                  Read more <ArrowRight className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PubVideo = ({ c }) => (
  <section className="py-16 px-4 bg-gray-900">
    <div className="max-w-4xl mx-auto">
      {c.title && <h2 className="text-3xl font-bold text-white mb-6 text-center">{c.title}</h2>}
      {c.url ? (
        <div className="aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
          <iframe src={c.url} className="w-full h-full" allowFullScreen title={c.title || 'Video'} />
        </div>
      ) : (
        <div className="aspect-video bg-gray-800 rounded-xl flex items-center justify-center">
          <PlayCircle className="h-20 w-20 text-gray-700" />
        </div>
      )}
    </div>
  </section>
);

export default PublicPage;
